import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiRequestError,
  downloadCultivationCloseXlsx,
  collectEarlierPlantMonths,
  getCultivationClose,
  getCurrentExtensionSourceDocument,
  previousCloseMonthEnd,
  priorPlantCountsFromClose,
  priorSettingsFromClose,
  type PriorCloseSettings,
  getExtensionDependencyImports,
  getExtensionViewAsset,
  getProfitAndLoss,
  listEntityReportLocations,
  listChartAccounts,
  listExtensionInstallationLogs,
  listExtensionInstallations,
  runExtensionImport,
  uploadExtensionSourceDocument,
  type ExtensionSourceDocumentUploadMetadata,
  type ExtensionInstallation,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { ExtensionV2ViewPage } from "./ExtensionV2ViewPage";
import { localNavigationPath } from "./extension-navigation";
export { localNavigationPath } from "./extension-navigation";
import {
  closePeriodFromMode,
  type ClosePeriod,
  type HostCloseBooks,
  loadAccountLedgerEvidence,
  loadCloseBooksEvidence,
  mergeCloseBooksEvidence,
  readCloseBooksAccountCodes,
} from "./host-ledger";

type BridgePayload = Record<string, unknown>;
type DeclaredDependency = { key: { publisher: string; key: string }; version: string };
type InstallationWithDependencies = ExtensionInstallation & {
  dependencies?: DeclaredDependency[];
};

const MAX_UI_MESSAGE_BYTES = 8 * 1024 * 1024;
const MAX_SOURCE_BYTES = 4 * 1024 * 1024;

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function boundedMessage(value: unknown): boolean {
  try {
    return JSON.stringify(value).length <= MAX_UI_MESSAGE_BYTES;
  } catch {
    return false;
  }
}

function safeErrorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return cause instanceof Error ? cause.message : "The extension request failed.";
}

function evidenceAccountRequest(payload: BridgePayload): {
  rawMaterialAccountCode: string;
  accountCodes: string[];
} {
  return {
    rawMaterialAccountCode:
      typeof payload.raw_material_account_code === "string"
        ? payload.raw_material_account_code
        : "",
    accountCodes: Array.isArray(payload.account_codes)
      ? payload.account_codes.filter((value): value is string => typeof value === "string")
      : [],
  };
}

function evidenceCacheKey(
  period: ClosePeriod,
  rawMaterialAccountCode: string,
  accountCodes: readonly string[],
): string {
  return JSON.stringify([
    period.start,
    period.end,
    rawMaterialAccountCode.trim(),
    [...new Set(accountCodes.map((code) => code.trim()).filter(Boolean))].sort(),
  ]);
}

async function sourceFile(payload: BridgePayload): Promise<File> {
  const file = payload.file as
    | { name?: unknown; size?: unknown; type?: unknown; arrayBuffer?: unknown }
    | null;
  if (
    !file ||
    typeof file.name !== "string" ||
    typeof file.size !== "number" ||
    typeof file.arrayBuffer !== "function" ||
    file.size === 0 ||
    file.size > MAX_SOURCE_BYTES
  ) {
    throw new Error("A file within the upload limit is required.");
  }
  const bytes = await (file as { arrayBuffer: () => Promise<ArrayBuffer> }).arrayBuffer();
  return new File([bytes], file.name, {
    type: typeof file.type === "string" ? file.type : "application/octet-stream",
  });
}

function sourceMetadata(payload: BridgePayload): ExtensionSourceDocumentUploadMetadata {
  const metadata = payload.metadata;
  if (!isObject(metadata)) throw new Error("Import metadata is required.");
  const text = (key: string) => {
    const value = metadata[key];
    if (typeof value !== "string" || !value) throw new Error(`Import ${key} is required.`);
    return value;
  };
  const correction = metadata.correction;
  if (
    correction !== undefined &&
    correction !== "new" &&
    correction !== "replace" &&
    correction !== "amend"
  ) {
    throw new Error("Import correction mode is invalid.");
  }
  return {
    profile: text("profile"),
    license_id: text("license_id"),
    period_start: text("period_start"),
    period_end: text("period_end"),
    ...(correction ? { correction } : {}),
    ...(typeof metadata.prior_document_id === "string" && metadata.prior_document_id
      ? { prior_document_id: metadata.prior_document_id }
      : {}),
  };
}

function dataUrl(text: string, type: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:${type};base64,${btoa(binary)}`;
}

const PROFIT_AND_LOSS_ROW_LIMIT = 500;
const PROFIT_AND_LOSS_TEXT_LIMIT = 500;

function closeMonths(period: ClosePeriod): string[] {
  const months: string[] = [];
  let cursor = period.start.slice(0, 7);
  const last = period.end.slice(0, 7);
  while (cursor <= last) {
    months.push(cursor);
    const [year, month] = cursor.split("-").map(Number);
    cursor = month === 12
      ? `${String(year + 1).padStart(4, "0")}-01`
      : `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}`;
  }
  return months;
}

function monthEnd(month: string): string {
  const [year, number] = month.split("-").map(Number);
  const day = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, "0")}`;
}

/** The close workbook needs one P&L column per close month. Fetch each
 * monthly statement through the same read surface as Reports, then merge
 * rows by section/account identity. Nothing is inferred from the ledger. */
export async function closeProfitAndLoss(period: ClosePeriod) {
  const merged = new Map<string, {
    row_id: string;
    kind: "account" | "subtotal" | "net";
    account_id: string | null;
    account_code: string | null;
    account_name: string;
    by_month_cents: Record<string, number>;
  }>();
  for (const month of closeMonths(period)) {
    const statement = await getProfitAndLoss({
      start_date: `${month}-01`,
      end_date: monthEnd(month),
      basis: "accrual",
    });
    for (const [sectionIndex, section] of statement.sections.entries()) {
      for (const [rowIndex, row] of section.rows.entries()) {
        const account = typeof row.code === "string" && row.code.trim() !== "";
        const amount = row.amount_cents ?? row.amounts_cents?.[0];
        if (typeof amount !== "number" || !Number.isSafeInteger(amount)) continue;
        const key = `s${sectionIndex}:${row.code ?? row.account_id ?? rowIndex}`;
        const name = row.name.slice(0, PROFIT_AND_LOSS_TEXT_LIMIT);
        const existing = merged.get(key);
        if (existing) existing.by_month_cents[month] = amount;
        else merged.set(key, {
          row_id: key,
          kind: account ? "account" : "subtotal",
          account_id: account ? (row.account_id ?? row.code ?? null) : null,
          account_code: account ? (row.code ?? null) : null,
          account_name: name,
          by_month_cents: { [month]: amount },
        });
      }
      if (typeof section.subtotal_cents === "number" && Number.isSafeInteger(section.subtotal_cents)) {
        const key = `section:${sectionIndex}`;
        const existing = merged.get(key);
        if (existing) existing.by_month_cents[month] = section.subtotal_cents;
        else merged.set(key, {
          row_id: key,
          kind: "subtotal",
          account_id: null,
          account_code: null,
          account_name: section.label.slice(0, PROFIT_AND_LOSS_TEXT_LIMIT),
          by_month_cents: { [month]: section.subtotal_cents },
        });
      }
    }
    if (typeof statement.grand_total_cents === "number" && Number.isSafeInteger(statement.grand_total_cents)) {
      const key = "net_income";
      const existing = merged.get(key);
      if (existing) existing.by_month_cents[month] = statement.grand_total_cents;
      else merged.set(key, {
        row_id: key,
        kind: "net",
        account_id: null,
        account_code: null,
        account_name: "Net Income",
        by_month_cents: { [month]: statement.grand_total_cents },
      });
    }
  }
  if (merged.size > PROFIT_AND_LOSS_ROW_LIMIT) {
    throw new Error(`The profit-and-loss statement has more than ${PROFIT_AND_LOSS_ROW_LIMIT} rows; the close preview is withheld.`);
  }
  return {
    accounting_basis: "accrual" as const,
    provenance: {
      origin: "Accounting monthly profit and loss",
      method: "posted ledger statements",
    },
    rows: [...merged.values()],
  };
}

export function ExtensionFrame({
  publisher,
  extensionKey: key,
  viewId,
  mode,
  reviewedExciseReview,
  onReviewed,
  className,
}: {
  publisher: string;
  extensionKey: string;
  viewId?: string;
  mode?: Record<string, unknown>;
  reviewedExciseReview?: unknown;
  onReviewed?: (inputs: unknown) => void | Promise<void>;
  className?: string;
}) {
  const { scope } = useOrganization();
  const frame = useRef<HTMLIFrameElement | null>(null);
  const evidenceCache = useRef(new Map<string, HostCloseBooks>());
  const [installations, setInstallations] = useState<ExtensionInstallation[] | null>(null);
  const [assets, setAssets] = useState<{ js: string; css: string; workbook: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [priorClose, setPriorClose] = useState<{
    month: string;
    input_identity: string;
    ending_counts: { mother: number; immature: number; vegetative: number; flowering: number };
  } | null>(null);
  const [priorSettings, setPriorSettings] = useState<PriorCloseSettings | null>(null);
  const [yearMonths, setYearMonths] = useState<Awaited<ReturnType<typeof collectEarlierPlantMonths>>>([]);

  const closeMode = Boolean(mode && isObject(mode.close));
  const hostMode = useMemo(
    () => mode && isObject(mode.close) && reviewedExciseReview !== undefined
      ? { ...mode, close: { ...mode.close, reviewed_excise_review: reviewedExciseReview } }
      : mode,
    [mode, reviewedExciseReview],
  );
  const installation = useMemo(
    () =>
      installations?.find(
        (item) =>
          item.state === "enabled" &&
          item.publisher === publisher &&
          item.key === key &&
          item.apps?.includes("niche-accounting") &&
          (item.api_version === "2.0" || Boolean(item.entrypoints.ui_view)),
      ) ?? null,
    [installations, publisher, key],
  );
  const v2 = installation?.api_version === "2.0";
  const v2ViewId = useMemo(() => {
    if (!v2 || !viewId || !installation?.v2) return null;
    return installation.v2.views?.some(
      (view) => Boolean(view) && typeof view === "object" && (view as { id?: unknown }).id === viewId,
    ) ? viewId : null;
  }, [installation, v2, viewId]);

  useEffect(() => {
    evidenceCache.current.clear();
  }, [installation?.id, scope?.organization_id, scope?.legal_entity_id]);

  useEffect(() => {
    if (!scope?.organization_id) return;
    let cancelled = false;
    setError(null);
    setInstallations(null);
    listExtensionInstallations(scope.organization_id)
      .then((rows) => {
        if (!cancelled) setInstallations(rows);
      })
      .catch((cause) => {
        if (!cancelled) setError(safeErrorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.organization_id, attempt]);

  useEffect(() => {
    if (!installation || v2 || !scope?.organization_id) return;
    let cancelled = false;
    setAssets(null);
    setError(null);
    const scriptPath = `dist/${installation.entrypoints.ui_view!.replace(/^src\//, "")}`;
    const stylePath = scriptPath.replace(/\.js$/, ".css");
    const workbookPath = scriptPath.replace(/\.js$/, ".workbook.js");
    Promise.all([
      getExtensionViewAsset(
        scope.organization_id,
        installation.id,
        scriptPath,
      ),
      getExtensionViewAsset(
        scope.organization_id,
        installation.id,
        stylePath,
      ).catch((cause) => {
        if (cause instanceof ApiRequestError && cause.status === 404) return "";
        throw cause;
      }),
      closeMode
        ? getExtensionViewAsset(
            scope.organization_id,
            installation.id,
            workbookPath,
          ).catch((cause) => {
            if (cause instanceof ApiRequestError && cause.status === 404) return "";
            throw cause;
          })
        : Promise.resolve(""),
    ])
      .then(async ([js, css, workbook]) => {
        if (cancelled) return;
        const close = hostMode && isObject(hostMode.close) ? hostMode.close : null;
        const previous = close && typeof close.period === "object" && close.period && typeof (close.period as { start?: unknown }).start === "string"
          ? previousCloseMonthEnd((close.period as { start: string }).start)
          : null;
        if (close && typeof close.license_id === "string" && previous) {
          try {
            const packet = await getCultivationClose({ license_id: close.license_id, period_end: previous.period_end });
            if (!cancelled) {
              setPriorClose(priorPlantCountsFromClose(packet, previous.month));
              setPriorSettings(priorSettingsFromClose(packet, previous.month));
              setYearMonths(await collectEarlierPlantMonths(close.license_id as string, (close.period as { start: string }).start, async (periodEnd) => {
                try {
                  return await getCultivationClose({ license_id: close.license_id as string, period_end: periodEnd });
                } catch {
                  return null;
                }
              }));
            }
          } catch (cause) {
            if (!(cause instanceof ApiRequestError) || (cause.status !== 404 && cause.status !== 409)) throw cause;
            if (!cancelled) {
              setPriorClose(null);
              setPriorSettings(null);
            }
          }
        }
        if (!cancelled) setAssets({ js, css, workbook });
      })
      .catch((cause) => {
        if (!cancelled) setError(safeErrorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [closeMode, hostMode, installation, scope?.organization_id, v2]);

  const contextMode = hostMode && isObject(hostMode.close) && (priorClose || priorSettings || yearMonths.length > 0)
    ? {
      ...hostMode,
      close: {
        ...hostMode.close,
        ...(priorClose ? { prior_close: { ...priorClose, current_input_identity: priorClose.input_identity } } : {}),
        ...(priorSettings ? { prior_settings: priorSettings } : {}),
        ...(yearMonths.length > 0 ? { year_to_date_months: yearMonths } : {}),
      },
    }
    : hostMode;
  const context = useMemo(
    () => ({
      ...(contextMode ? { mode: contextMode } : {}),
      scope: scope
        ? {
            organization_id: scope.organization_id,
            legal_entity_id: scope.legal_entity_id,
          }
        : null,
      installation: installation
        ? {
            id: installation.id,
            publisher: installation.publisher,
            key: installation.key,
            version: installation.version,
            entrypoints: installation.entrypoints,
          }
        : null,
      installations: (installations ?? []).map((item) => ({
        id: item.id,
        publisher: item.publisher,
        key: item.key,
        version: item.version,
        state: item.state,
        apps: item.apps,
        entrypoints: item.entrypoints,
        dependencies: (item as InstallationWithDependencies).dependencies ?? [],
        ui: item.ui ?? [],
      })),
    }),
    [contextMode, installation, installations, scope],
  );
  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(() => {
    if (!installation || !scope) return;
    const current = installation as InstallationWithDependencies;
    const allowedSources = new Set([current.id]);
    for (const dependency of current.dependencies ?? []) {
      const installedDependency = (installations ?? []).find(
        (item) =>
          item.state === "enabled" &&
          item.publisher === dependency.key.publisher &&
          item.key === dependency.key.key,
      );
      if (installedDependency) allowedSources.add(installedDependency.id);
    }
    const requireInstallation = (value: unknown): string => {
      if (typeof value !== "string" || !allowedSources.has(value)) {
        throw new Error("The requested extension installation is not a declared dependency.");
      }
      return value;
    };
    const closeEvidence = async (
      period: ClosePeriod,
      rawMaterialAccountCode: string,
      accountCodes: readonly string[],
    ): Promise<HostCloseBooks> => {
      const key = evidenceCacheKey(period, rawMaterialAccountCode, accountCodes);
      const cached = evidenceCache.current.get(key);
      if (cached) return cached;
      const evidence = await loadCloseBooksEvidence({
        period,
        rawMaterialAccountCode,
        accountCodes,
      });
      evidenceCache.current.set(key, evidence);
      return evidence;
    };
    const dispatch = async (command: string, payload: BridgePayload): Promise<unknown> => {
      switch (command) {
        case "installations":
          return contextRef.current.installations;
        case "licenses":
          return listEntityReportLocations(scope.organization_id, scope.legal_entity_id);
        case "accounts":
          return listChartAccounts();
        case "ledger.lines": {
          const code = typeof payload.account_code === "string" ? payload.account_code.trim() : "";
          if (!code) throw new Error("An account code is required.");
          const period = closePeriodFromMode(contextRef.current.mode);
          if (!period) throw new Error("Ledger reads require the close-review period.");
          const evidence = await loadAccountLedgerEvidence(code, period);
          return {
            complete: evidence.complete,
            ledger_lines: evidence.ledger_lines,
            warnings: evidence.warnings,
          };
        }
        case "close.evidence": {
          const period = closePeriodFromMode(contextRef.current.mode);
          if (!period) throw new Error("Close evidence requires the close-review period.");
          const request = evidenceAccountRequest(payload);
          const evidence = await closeEvidence(
            period,
            request.rawMaterialAccountCode,
            request.accountCodes,
          );
          return {
            books: {
              complete: evidence.complete,
              raw_material_account_code: evidence.raw_material_account_code,
              ledger_lines: evidence.ledger_lines,
            },
            warnings: evidence.warnings,
          };
        }
        case "source.current": {
          evidenceCache.current.clear();
          const installationId = requireInstallation(payload.installation_id);
          const profile = typeof payload.profile === "string" ? payload.profile : "";
          const licenseId = typeof payload.license_id === "string" ? payload.license_id : "";
          const periodStart = typeof payload.period_start === "string" ? payload.period_start : "";
          const periodEnd = typeof payload.period_end === "string" ? payload.period_end : "";
          if (!profile || !licenseId || !periodStart || !periodEnd) {
            throw new Error("Profile, license, and period are required.");
          }
          return getCurrentExtensionSourceDocument(scope.organization_id, installationId, {
            organization_id: scope.organization_id,
            profile,
            license_id: licenseId,
            period_start: periodStart,
            period_end: periodEnd,
          });
        }
        case "dependency.imports": {
          const period = closePeriodFromMode(contextRef.current.mode);
          if (!period) throw new Error("Dependency imports are only available in the close-review surface.");
          const installationId = requireInstallation(payload.installation_id);
          const snapshot = await getExtensionDependencyImports(
            scope.organization_id,
            installationId,
          );
          const request = evidenceAccountRequest(payload);
          if (!request.rawMaterialAccountCode.trim() && request.accountCodes.length === 0) {
            return snapshot;
          }
          const evidence = await closeEvidence(
            period,
            request.rawMaterialAccountCode,
            request.accountCodes,
          );
          return {
            ...snapshot,
            books: {
              complete: evidence.complete,
              raw_material_account_code: evidence.raw_material_account_code,
              ledger_lines: evidence.ledger_lines,
            },
            warnings: evidence.warnings,
          };
        }
        case "source.upload": {
          const installationId = requireInstallation(payload.installation_id);
          return uploadExtensionSourceDocument(
            scope.organization_id,
            installationId,
            sourceMetadata(payload),
            await sourceFile(payload),
          );
        }
        case "source.run": {
          evidenceCache.current.clear();
          const installationId = requireInstallation(payload.installation_id);
          if (typeof payload.document_id !== "string" || !payload.document_id) {
            throw new Error("A source document is required.");
          }
          return runExtensionImport(
            scope.organization_id,
            installationId,
            payload.document_id,
          );
        }
        case "source.logs": {
          const installationId = requireInstallation(payload.installation_id);
          return listExtensionInstallationLogs(
            scope.organization_id,
            installationId,
            { trigger: "import_profile" },
          );
        }
        case "navigate": {
          if (typeof payload.path !== "string" || !payload.path.startsWith("/") || payload.path.startsWith("//")) {
            throw new Error("A local navigation path is required.");
          }
          window.location.assign(localNavigationPath(payload.path, scope));
          return { ok: true };
        }
        case "close.profit_and_loss": {
          const period = closePeriodFromMode(contextRef.current.mode);
          if (!period) throw new Error("Profit and loss requires the close-review period.");
          return { profit_and_loss: await closeProfitAndLoss(period) };
        }
        case "close.prior_ending": {
          const close = isObject(contextRef.current.mode) && isObject(contextRef.current.mode.close)
            ? contextRef.current.mode.close
            : null;
          if (!close || typeof close.license_id !== "string" || !isObject(close.period) || typeof close.period.start !== "string") {
            return { available: false };
          }
          const previous = previousCloseMonthEnd(close.period.start);
          if (!previous) return { available: false };
          const actor = typeof close.actor_ref === "string" ? close.actor_ref : undefined;
          try {
            const packet = await getCultivationClose({ license_id: close.license_id, period_end: previous.period_end });
            const ending = priorPlantCountsFromClose(packet, previous.month);
            if (!ending) return { available: false };
            return { available: true, ...ending, ...(actor ? { actor_ref: actor } : {}) };
          } catch (cause) {
            if (cause instanceof ApiRequestError && (cause.status === 404 || cause.status === 409)) return { available: false };
            throw cause;
          }
        }
        case "close.download": {
          const close = isObject(contextRef.current.mode) && isObject(contextRef.current.mode.close)
            ? contextRef.current.mode.close
            : null;
          if (!close || typeof close.license_id !== "string" || !isObject(close.period) ||
              typeof close.period.end !== "string") {
            throw new Error("Close download requires the period-lock context.");
          }
          await downloadCultivationCloseXlsx({
            license_id: close.license_id,
            period_end: close.period.end,
          });
          return { ok: true };
        }
        case "close.reviewed": {
          if (!onReviewed) throw new Error("This extension is not in a close-review surface.");
          if (!isObject(payload.inputs)) throw new Error("Reviewed close inputs are required.");
          const reviewInputs = isObject(payload.inputs.review_inputs) ? payload.inputs.review_inputs : null;
          const suppliedPrior = reviewInputs && isObject(reviewInputs.prior_close_ending) ? reviewInputs.prior_close_ending : null;
          const close = isObject(contextRef.current.mode) && isObject(contextRef.current.mode.close) ? contextRef.current.mode.close : null;
          const previous = close && isObject(close.period) && typeof close.period.start === "string" ? previousCloseMonthEnd(close.period.start) : null;
          if (suppliedPrior && close && typeof close.license_id === "string" && previous) {
            try {
              const packet = await getCultivationClose({ license_id: close.license_id, period_end: previous.period_end });
              const live = priorPlantCountsFromClose(packet, previous.month);
              if (!live || live.input_identity !== suppliedPrior.input_identity) throw new Error("The prior close changed after these opening counts were inherited.");
              suppliedPrior.current_input_identity = live.input_identity;
            } catch (cause) {
              if (cause instanceof ApiRequestError && (cause.status === 404 || cause.status === 409)) throw new Error("The prior close is no longer available.");
              throw cause;
            }
          }
          const period = closePeriodFromMode(contextRef.current.mode);
          const books = isObject(payload.inputs.books) ? payload.inputs.books : null;
          const completeBooks =
            books?.complete === true && Array.isArray(books.ledger_lines);
          const accounts = readCloseBooksAccountCodes(payload.inputs);
          if (
            period &&
            !completeBooks &&
            (accounts.rawMaterialAccountCode || accounts.accountCodes.length > 0)
          ) {
            const evidence = await closeEvidence(
              period,
              accounts.rawMaterialAccountCode,
              accounts.accountCodes,
            );
            await onReviewed(mergeCloseBooksEvidence(payload.inputs, evidence));
            evidenceCache.current.clear();
            return { ok: true };
          }
          await onReviewed(payload.inputs);
          evidenceCache.current.clear();
          return { ok: true };
        }
        default:
          throw new Error("That extension command is not supported.");
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      if (!isObject(event.data) || !boundedMessage(event.data)) return;
      if (event.data.type === "sylviq.ready") {
        frame.current?.contentWindow?.postMessage(
          { type: "sylviq.context", value: contextRef.current },
          "*",
        );
        return;
      }
      if (event.data.type !== "sylviq.command") return;
      const id = event.data.id;
      const command = event.data.command;
      const payload = isObject(event.data.payload) ? event.data.payload : {};
      if (typeof id !== "string" || !id || id.length > 120) return;
      if (typeof command !== "string" || !command || command.length > 120) return;
      void dispatch(command, payload)
        .then((value) => {
          frame.current?.contentWindow?.postMessage(
            { type: "sylviq.response", id, ok: true, value },
            "*",
          );
        })
        .catch((cause) => {
          frame.current?.contentWindow?.postMessage(
            { type: "sylviq.response", id, ok: false, error: safeErrorMessage(cause) },
            "*",
          );
        });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [installation, installations, onReviewed, scope]);

  const document = useMemo(() => {
    if (!assets) return "";
    const csp =
      "default-src 'none'; script-src data:; style-src data:; img-src data:; connect-src 'none'; form-action 'none'; base-uri 'none'";
    const css = dataUrl(assets.css, "text/css");
    const workbook = dataUrl(assets.workbook, "application/javascript");
    const js = dataUrl(assets.js, "application/javascript");
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="${csp}"><link rel="stylesheet" href="${css}"></head><body><div id="sylviq-extension-root"></div><script src="${workbook}"></script><script src="${js}"></script></body></html>`;
  }, [assets]);

  if (error) {
    return (
      <div className="grid gap-4">
        <PageHeader title="Extension" description="The installed extension page could not be opened." />
        <EmptyState
          title="Extension page unavailable"
          reason={error}
          action={<Button type="button" variant="secondary" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}
        />
      </div>
    );
  }

  if (installations === null || (installation && !v2 && !assets)) {
    return (
      <div className="grid gap-4">
        <PageHeader title="Extension" description="Loading the installed extension…" />
        <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      </div>
    );
  }

  if (!installation) {
    return (
      <EmptyState
        title="Extension page is not installed"
        reason={`${publisher}/${key} does not provide an enabled Accounting workspace in this organization.`}
      />
    );
  }

  if (v2) {
    if (!v2ViewId) {
      return (
        <EmptyState
          title="Extension view is unavailable"
          reason={`${publisher}/${key} does not declare the requested extension view.`}
        />
      );
    }
    return <ExtensionV2ViewPage installationId={installation.id} viewId={v2ViewId} />;
  }

  return (
    <iframe
      ref={frame}
      title={`${installation.publisher}/${installation.key}`}
      sandbox="allow-scripts"
      srcDoc={document}
      onLoad={() =>
        frame.current?.contentWindow?.postMessage(
          { type: "sylviq.context", value: contextRef.current },
          "*",
        )
      }
      className={
        className ??
        "min-h-[calc(100vh-7rem)] w-full rounded-2xl border border-[var(--border)] bg-[var(--surface)]"
      }
    />
  );
}
